// // src/pages/about/index.js
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Col, Row } from "reactstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import Eta from "../../components/general/Eta";
import Banner from "../../components/general/Banner";
import { fetchAbout } from "../../redux/slices/aboutSlice";
import { fetchSeo } from "../../redux/slices/seoSlice";
import API from "../../api";

// Dil kontrolü için yardımcı fonksiyon - güncellendi
const hasContentForLanguage = (item, language) => {
  return (
    item?.title?.[language] ||
    item?.description?.[language] ||
    item?.content?.[language] ||
    item?.specialField1?.[language]
  );
};

// Resim kontrolü için yeni fonksiyon
const hasOnlyImages = (item) => {
  return (
    item.files?.length > 0 && // resim var
    !item.title?.tr &&
    !item.title?.en && // başlık yok
    !item.description?.tr &&
    !item.description?.en && // açıklama yok
    !item.content?.tr &&
    !item.content?.en && // içerik yok
    !item.specialField1?.tr &&
    !item.specialField1?.en // özel alan yok
  );
};

const hasOnlyText = (item, language) => {
  return (
    (!item.files || item.files.length === 0) && // resim yok
    hasContentForLanguage(item, language) // seçili dilde içerik var
  );
};

// Boş içerik kontrolü için yeni fonksiyon
const hasEmptyContent = (elements) => {
  return elements.every(
    (element) =>
      !element.content ||
      element.content === "" ||
      (typeof element.content === "string" && element.content.trim() === "")
  );
};

const About = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = useSelector((state) => state.lang.lang);
  const { dataAbout } = useSelector((state) => state.about);
  const { dataContent } = useSelector((state) => state.content);
  const { dataSeo } = useSelector((state) => state.seo);
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    dispatch(fetchAbout());
    dispatch(fetchSeo());
  }, [dispatch]);

  useEffect(() => {
    const setSeoContent = async () => {
      if (dataSeo?.length > 0) {
        const aboutData = dataSeo.find(
          (item) =>
            item.title[lang] === "Hakkımızda" || item.title[lang] === "About us"
        );
        if (aboutData) {
          setSeoData(aboutData);
          document.title = aboutData.title[lang];
        }
      }
    };

    setSeoContent();
  }, [dataSeo, lang]);

  const sortedData = dataAbout
    ? [...dataAbout].sort((a, b) => a.sort - b.sort)
    : [];
  let bannerText = dataContent?.bannerText2?.[lang] || "";

  // Elementleri sort değerine göre sırala
  const getSortedElements = (item) => {
    const elements = [];

    // İçeriğin gösterilip gösterilmeyeceğini kontrol et
    const shouldShow =
      hasContentForLanguage(item, lang) || // Dil içeriği var
      hasOnlyImages(item); // Sadece resim var

    if (!shouldShow) {
      return elements;
    }

    // Title ekle
    if (item.title && item.titleCol) {
      elements.push({
        type: "title",
        content: item.title[lang],
        col: item.titleCol,
        sort: item.titleSort || 0,
      });
    }

    // Description ekle
    if (item.description && item.descriptionCol) {
      elements.push({
        type: "description",
        content: item.description[lang],
        col: item.descriptionCol,
        sort: item.descriptionSort || 0,
      });
    }

    // SpecialField1 ekle
    if (item.specialField1 && item.specialField1Col) {
      elements.push({
        type: "specialField1",
        content: item.specialField1[lang],
        col: item.specialField1Col,
        sort: item.specialField1Sort || 0,
      });
    }

    // Content ekle
    if (item.content && item.contentCol) {
      elements.push({
        type: "content",
        content: item.content[lang],
        col: item.contentCol,
        sort: item.contentSort || 0,
      });
    }

    // Images ekle - her durumda ekle
    if (item.files && item.filesCol) {
      item.files.forEach((file, index) => {
        elements.push({
          type: "image",
          content: file,
          col: item.filesCol[index],
          sort: (item.filesSort && item.filesSort[index]) || index,
        });
      });
    }

    return elements.sort((a, b) => a.sort - b.sort);
  };

  return (
    <Eta>
      {seoData && (
        <Helmet>
          <title>{seoData.title[lang]}</title>
          <meta name="description" content={seoData.description[lang]} />
          {seoData.keywords[lang] && (
            <meta name="keywords" content={seoData.keywords[lang]} />
          )}
          {seoData.canonical && (
            <link rel="canonical" href={seoData.canonical} />
          )}
          {seoData.author && <meta name="author" content={seoData.author} />}
        </Helmet>
      )}
      <Banner description={bannerText} />
      <div className="about-page">
        {sortedData.map((item, idx) => {
          const sortedElements = getSortedElements(item);
          const imageElements = sortedElements.filter(
            (el) => el.type === "image"
          );

          // İçerik tamamen boşsa hiçbir şey render etme
          if (hasEmptyContent(sortedElements) && imageElements.length === 0) {
            return null;
          }

          // 4'ten fazla resim varsa ve dil uygunsa Swiper kullan
          if (imageElements.length >= 4 && hasContentForLanguage(item, lang)) {
            return (
              <Row key={idx} className="h-100">
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="mb-4 first"
                >
                  <div className="d-flex flex-column h-100">
                    <div className="flex-grow-1">
                      {sortedElements
                        .filter((el) => el.type !== "image")
                        .map((element, index) => (
                          <div key={index}>
                            {element.type === "title" && (
                              <strong className="f20 fw700 color1">
                                {element.content}
                              </strong>
                            )}
                            {element.type === "description" && (
                              <strong className="f20 fw400 color2">
                                {element.content}
                              </strong>
                            )}
                            {element.type === "specialField1" && (
                              <strong className="ml-2 f20 fw400 color4">
                                {element.content}
                              </strong>
                            )}
                            {element.type === "content" && (
                              <div className="mt-3 f14">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: element.content,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                    <div className="mt-4 mb-5">
                      <Swiper
                        modules={[Navigation, Pagination, Autoplay]}
                        slidesPerView={1}
                        spaceBetween={20}
                        loop
                        autoplay={{
                          delay: 3000,
                          disableOnInteraction: false,
                        }}
                        breakpoints={{
                          // xs ve sm breakpoint'lerde (576px altı) - 1 görüntü
                          0: {
                            slidesPerView: 1,
                          },
                          // sm breakpoint (576px üstü) - 2 görüntü
                          576: {
                            slidesPerView: 2,
                          },
                          // md breakpoint (768px üstü) - 3 görüntü
                          768: {
                            slidesPerView: 3,
                          },
                          // lg breakpoint (992px üstü) - 3 görüntü
                          992: {
                            slidesPerView: 3,
                          },
                          // xl breakpoint (1200px üstü) - 4 görüntü
                          1200: {
                            slidesPerView: 4,
                          },
                        }}
                      >
                        {imageElements.map((element, index) => (
                          <SwiperSlide key={index} className="text-center">
                            <img
                              src={`${API.BASE}${element.content}`}
                              alt={`Reference ${index}`}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                </Col>
              </Row>
            );
          }

          // Normal görünüm - içerik boş değilse
          const textElements = sortedElements.filter(
            (el) => el.type !== "image"
          );
          const hasContent = !hasEmptyContent(textElements);

          return (
            <Row key={idx} className={hasContent ? "mb-4" : ""}>
              {sortedElements.map((element, index) => {
                // Eğer element boşsa render etme
                if (!element.content || element.content === "") {
                  return null;
                }

                // Sadece tek bir fotoğraf varsa ve başka içerik yoksa
                if (
                  hasOnlyImages(item) &&
                  item.files.length === 1 &&
                  element.type === "image"
                ) {
                  return (
                    <Col
                      key={index}
                      xs={12}
                      sm={12}
                      md={6}
                      className="mx-auto text-center mb-4"
                    >
                      <img
                        src={`${API.BASE}${element.content}`}
                        alt=""
                        className="w-100"
                      />
                    </Col>
                  );
                }

                // Normal görünüm için render kodunu devam ettir
                return (
                  <Col
                    key={index}
                    xs={12}
                    sm={12}
                    md={12}
                    l={element.col}
                    xl={element.col}
                    className={element.content ? "mb-4" : ""}
                  >
                    {element.type === "title" && element.content && (
                      <strong className="f20 fw700 color1">
                        {element.content}
                      </strong>
                    )}
                    {element.type === "description" && element.content && (
                      <div className="d-flex align-items-center">
                        <strong className="f20 fw400 color2">
                          {element.content}
                        </strong>
                        {sortedElements.find(
                          (el) => el.type === "specialField1"
                        )?.content && (
                          <strong className="ml-2 f20 fw400 color4">
                            {
                              sortedElements.find(
                                (el) => el.type === "specialField1"
                              ).content
                            }
                          </strong>
                        )}
                      </div>
                    )}
                    {/* specialField1'i ayrıca render etme, description ile birlikte render edildi */}
                    {element.type === "content" && element.content && (
                      <div className="mt-3 f14">
                        <div
                          dangerouslySetInnerHTML={{ __html: element.content }}
                        />
                      </div>
                    )}
                    {element.type === "image" && element.content && (
                      <img
                        src={`${API.BASE}${element.content}`}
                        alt=""
                        className="w-100"
                      />
                    )}
                  </Col>
                );
              })}
            </Row>
          );
        })}
      </div>
    </Eta>
  );
};

export default About;
